// SUB-OFFICE 7 — input. Desktop WASD/arrows + ONE interact verb (E/Space),
// all hard-gated by the FSM; keys are dead outside their states and dead the
// moment any DOM control owns focus (compose owns the keyboard absolutely).
// Mouse is a cursor only — the camera never rotates under player control.
// Touch ships in the mobile slice; the stub keeps the contract visible.

import { PerspectiveCamera, Vector3 } from 'three';
import { INTERACTABLES, type Interactable } from '../world/layout';
import type { EventBus } from '../entities/entities';
import type { Player } from '../entities/player';
import type { Fsm } from '../state';
import type { DomUi } from '../ui/dom';

const REACH = 0.9; // added to each interactable's radius for E-range

function textEntryFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'TEXTAREA' || tag === 'INPUT' || (el as HTMLElement).isContentEditable;
}

export class InputManager {
  private keys = new Set<string>();
  private ndc = { x: 0, y: 0, has: false };
  private hoverId: string | null = null;
  /** boot wires this: returns FP look yaw while the FP vantage is active, else null. */
  fpYaw: () => number | null = () => null;
  private rayO = new Vector3();
  private rayD = new Vector3();
  private tmp = new Vector3();

  constructor(
    private bus: EventBus,
    private fsm: Fsm,
    private player: Player,
    private camera: PerspectiveCamera,
    private dom: DomUi
  ) {
    window.addEventListener('keydown', (e) => this.onKeyDown(e));
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('blur', () => this.keys.clear());
    window.addEventListener('pointermove', (e) => {
      this.ndc.x = (e.clientX / window.innerWidth) * 2 - 1;
      this.ndc.y = -(e.clientY / window.innerHeight) * 2 + 1;
      this.ndc.has = true;
    });
    window.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || textEntryFocused()) return;
      if (this.hoverId && this.fsm.interactAllowed()) {
        const it = INTERACTABLES.find((i) => i.id === this.hoverId);
        if (it && this.inReach(it)) this.bus.emit('interact', { id: it.id });
      }
    });
    window.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (textEntryFocused()) return; // compose owns the keyboard absolutely
    if (e.code === 'Backquote') {
      this.bus.emit('debug');
      return;
    }
    if (e.code === 'KeyM') {
      this.bus.emit('audio:toggle');
      return;
    }
    // view_policy: first-person is the only player vantage. The top-down rig
    // survives as an internal cinematic tool (intake, ceremonies, capture) but
    // is no longer player-switchable — there is no V key.
    if (e.code === 'Space') e.preventDefault(); // Space never scrolls the hall
    if ((e.code === 'KeyE' || e.code === 'Space') && !e.repeat) {
      if (!this.fsm.interactAllowed()) return;
      const it = this.nearest();
      if (it) this.bus.emit('interact', { id: it.id });
      return;
    }
    if (e.code === 'Escape') {
      this.bus.emit('escape'); // overlays pop here in later slices
      return;
    }
    this.keys.add(e.code);
  }

  private inReach(it: Interactable): boolean {
    const dx = it.x - this.player.pos.x;
    const dz = it.z - this.player.pos.z;
    return dx * dx + dz * dz <= (it.r + REACH) * (it.r + REACH);
  }

  private nearest(): Interactable | null {
    let best: Interactable | null = null;
    let bestD = Infinity;
    for (const it of INTERACTABLES) {
      const dx = it.x - this.player.pos.x;
      const dz = it.z - this.player.pos.z;
      const d2 = dx * dx + dz * dz;
      const rr = (it.r + REACH) * (it.r + REACH);
      if (d2 <= rr && d2 < bestD) {
        bestD = d2;
        best = it;
      }
    }
    return best;
  }

  /** Cursor raycast vs proxy spheres — no meshes, just math.
   *  In FP the cursor is the center-screen crosshair. */
  private raycastHover(fp: boolean): Interactable | null {
    if (!fp && !this.ndc.has) return null;
    const nx = fp ? 0 : this.ndc.x;
    const ny = fp ? 0 : this.ndc.y;
    this.rayO.copy(this.camera.position);
    this.rayD.set(nx, ny, 0.5).unproject(this.camera).sub(this.rayO).normalize();
    let best: Interactable | null = null;
    let bestT = Infinity;
    for (const it of INTERACTABLES) {
      this.tmp.set(it.x, it.y, it.z).sub(this.rayO);
      const t = this.tmp.dot(this.rayD);
      if (t < 0 || t > 80 || t > bestT) continue;
      const d2 = this.tmp.lengthSq() - t * t;
      if (d2 <= it.r * it.r) {
        bestT = t;
        best = it;
      }
    }
    return best;
  }

  /** Per-frame: movement vector through FSM gates + context prompt upkeep. */
  update(): void {
    let mx = 0;
    let mz = 0;
    if (this.fsm.movementAllowed() && !textEntryFocused()) {
      if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) mz -= 1;
      if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) mz += 1;
      if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) mx -= 1;
      if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) mx += 1;
    }
    // FP: movement is relative to the look direction, not the world axes
    const yaw = this.fpYaw();
    if (yaw !== null && (mx !== 0 || mz !== 0)) {
      const c = Math.cos(yaw);
      const s = Math.sin(yaw);
      const wx = mx * c + mz * s;
      const wz = -mx * s + mz * c;
      mx = wx;
      mz = wz;
      this.player.setFacing(yaw + Math.PI);
    }
    this.player.setMove(mx, mz);

    // context line: nearest-in-reach wins; cursor hover seconds
    const near = this.fsm.interactAllowed() ? this.nearest() : null;
    const hover = near ? null : this.raycastHover(yaw !== null);
    this.hoverId = (near ?? hover)?.id ?? null;
    this.dom.setPrompt(near ? near.prompt : hover ? hover.prompt : null);
  }
}

/** Touch controls land in the mobile slice (M12); the contract is fixed:
 *  dynamic-origin left-half stick (48px deadzone), >=64px context button,
 *  pointerId multi-touch, touch-action:none hardening. */
export class TouchStub {}
